/**
 * Monitoring and Analytics for Profile ID/Token Mismatches
 *
 * This module provides structured logging and metrics collection for tracking
 * when and where userId/access_token mismatches occur in the application.
 *
 * These logs can be sent to analytics platforms (DataDog, Sentry, etc.) or
 * stored in a database for analysis.
 */

export type MismatchSeverity = 'info' | 'warning' | 'error' | 'critical';

export type MismatchLocation =
  | 'UserContext:bootstrap'
  | 'UserContext:manual-sync'
  | 'PreTestForm:post-creation'
  | 'VoiceTestPage:lock'
  | 'PostTestForm:defensive-guard';

export interface MismatchEvent {
  // Event metadata
  timestamp: string;
  location: MismatchLocation;
  severity: MismatchSeverity;

  // ID mismatch details
  staleUserId: string | null;
  correctUserId: string;
  accessToken: string; // Truncated for logging

  // Session context
  sessionId?: string | null;

  // User context
  userAgent?: string;
  pathname?: string;

  // Resolution
  wasCorrected: boolean;
  correctionMethod: string;
}

export interface SyncMetrics {
  // Performance metrics
  syncDuration: number; // milliseconds

  // Outcome
  success: boolean;
  errorCode?: string;

  // Context
  location: MismatchLocation;
  hadMismatch: boolean;
}

/**
 * Central monitoring logger for ID mismatch events
 */
export class ProfileSyncMonitor {
  private static instance: ProfileSyncMonitor;
  private events: MismatchEvent[] = [];
  private metrics: SyncMetrics[] = [];

  private constructor() {}

  static getInstance(): ProfileSyncMonitor {
    if (!ProfileSyncMonitor.instance) {
      ProfileSyncMonitor.instance = new ProfileSyncMonitor();
    }
    return ProfileSyncMonitor.instance;
  }

  /**
   * Log an ID mismatch event
   */
  logMismatch(event: Omit<MismatchEvent, 'timestamp'>): void {
    const fullEvent: MismatchEvent = {
      ...event,
      timestamp: new Date().toISOString(),
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : undefined,
      pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
    };

    this.events.push(fullEvent);

    // Log to console with structured format for easy parsing
    const emoji = this.getSeverityEmoji(event.severity);
    console.warn(`${emoji} PROFILE_SYNC_MISMATCH`, {
      location: event.location,
      severity: event.severity,
      staleId: event.staleUserId,
      correctId: event.correctUserId,
      corrected: event.wasCorrected,
      session: event.sessionId,
      timestamp: fullEvent.timestamp,
    });

    // Send to analytics platform (placeholder for future integration)
    this.sendToAnalytics(fullEvent);
  }

  /**
   * Log sync performance metrics
   */
  logSyncMetrics(metrics: SyncMetrics): void {
    this.metrics.push(metrics);

    // Only log slow syncs or failures to reduce noise
    if (!metrics.success || metrics.syncDuration > 1000) {
      console.log(`⏱️ PROFILE_SYNC_METRICS`, {
        location: metrics.location,
        duration: `${metrics.syncDuration}ms`,
        success: metrics.success,
        hadMismatch: metrics.hadMismatch,
        error: metrics.errorCode,
      });
    }
  }

  /**
   * Get all recorded mismatch events (for debugging/admin dashboard)
   */
  getEvents(): MismatchEvent[] {
    return [...this.events];
  }

  /**
   * Get sync performance metrics (for monitoring)
   */
  getMetrics(): SyncMetrics[] {
    return [...this.metrics];
  }

  /**
   * Get summary statistics for monitoring dashboard
   */
  getSummary() {
    const totalEvents = this.events.length;
    const correctedEvents = this.events.filter(e => e.wasCorrected).length;
    const criticalEvents = this.events.filter(e => e.severity === 'critical').length;

    const locationBreakdown = this.events.reduce((acc, event) => {
      acc[event.location] = (acc[event.location] || 0) + 1;
      return acc;
    }, {} as Record<MismatchLocation, number>);

    const avgSyncDuration = this.metrics.length > 0
      ? this.metrics.reduce((sum, m) => sum + m.syncDuration, 0) / this.metrics.length
      : 0;

    const failedSyncs = this.metrics.filter(m => !m.success).length;

    return {
      mismatches: {
        total: totalEvents,
        corrected: correctedEvents,
        critical: criticalEvents,
        byLocation: locationBreakdown,
      },
      performance: {
        totalSyncs: this.metrics.length,
        avgDuration: Math.round(avgSyncDuration),
        failedSyncs,
        successRate: this.metrics.length > 0
          ? ((this.metrics.length - failedSyncs) / this.metrics.length * 100).toFixed(1) + '%'
          : 'N/A',
      },
    };
  }

  /**
   * Clear all events and metrics (for testing)
   */
  clear(): void {
    this.events = [];
    this.metrics = [];
  }

  private getSeverityEmoji(severity: MismatchSeverity): string {
    switch (severity) {
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'error': return '❌';
      case 'critical': return '🚨';
    }
  }

  private sendToAnalytics(_event: MismatchEvent): void {
    // Placeholder for future integration with analytics platforms
    // Example integrations:
    // - Sentry: Sentry.captureMessage('Profile ID Mismatch', { extra: event });
    // - DataDog: datadogRum.addAction('profile_mismatch', event);
    // - PostHog: posthog.capture('profile_mismatch', event);

    // For now, we could send to our own API endpoint for storage
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
      // Uncomment when ready to implement server-side logging
      /*
      fetch('/api/monitoring/mismatch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      }).catch(err => {
        console.error('Failed to send mismatch event to server:', err);
      });
      */
    }
  }
}

/**
 * Convenience function to log a mismatch event
 */
export function logMismatch(
  location: MismatchLocation,
  staleUserId: string | null,
  correctUserId: string,
  options: {
    severity?: MismatchSeverity;
    sessionId?: string | null;
    wasCorrected?: boolean;
    correctionMethod?: string;
  } = {}
): void {
  const monitor = ProfileSyncMonitor.getInstance();

  monitor.logMismatch({
    location,
    staleUserId,
    correctUserId,
    accessToken: truncateToken(typeof window !== 'undefined' ? localStorage.getItem('user_access_token') : null),
    severity: options.severity || 'warning',
    sessionId: options.sessionId,
    wasCorrected: options.wasCorrected ?? true,
    correctionMethod: options.correctionMethod || 'automatic',
  });
}

/**
 * Convenience function to log sync metrics
 */
export function logSyncMetrics(
  location: MismatchLocation,
  startTime: number,
  options: {
    success?: boolean;
    errorCode?: string;
    hadMismatch?: boolean;
  } = {}
): void {
  const monitor = ProfileSyncMonitor.getInstance();

  monitor.logSyncMetrics({
    location,
    syncDuration: Date.now() - startTime,
    success: options.success ?? true,
    errorCode: options.errorCode,
    hadMismatch: options.hadMismatch ?? false,
  });
}

/**
 * Get monitoring summary for admin dashboard
 */
export function getMonitoringSummary() {
  return ProfileSyncMonitor.getInstance().getSummary();
}

/**
 * Truncate access token for logging (show first/last 4 chars only)
 */
function truncateToken(token: string | null): string {
  if (!token || token.length < 12) return '***';
  return `${token.slice(0, 4)}...${token.slice(-4)}`;
}
